import React, { useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef } from 'react';
import {
    ReactFlow,
    ReactFlowProvider,
    ViewportPortal,
    useNodesState,
    useReactFlow,
    type Connection,
    type Edge,
    type Viewport as FlowViewport,
} from '@xyflow/react';
import { SkillData } from '@skill-trees/shared';
import { SkillPanelInteractor } from '../skill-panel/SkillPanelInteractor';
import { SkillNodeView, type SkillFlowNode } from './SkillNodeView';
import { SkillEdgeView } from './SkillEdgeView';
import { SkillConnectionLine } from './SkillConnectionLine';
import { ArrowMarkerDefs } from './ArrowMarkerDefs';
import { NODE_DIMENSIONS, VIEWPORT_ZOOM_LIMITS } from './skillTreeStyles';
import '@xyflow/react/dist/style.css';

export interface SkillTreeHandle {
    /**
     * Animate the viewport via React Flow's internal d3 transition — no per-frame app-state
     * updates. Resolves true when the animation runs to completion; false when a user
     * gesture interrupts it or a newer animateTo supersedes it, so whatever the animation
     * was gating can settle instead of hanging on d3's never-resolving interrupted promise.
     * The final viewport is reported once through onViewportChange (via onMoveEnd).
     */
    animateTo(viewport: { x: number; y: number; k: number }, duration: number): Promise<boolean>;
    /** Live React Flow viewport — app state can lag behind it mid-animation. */
    getViewport(): { x: number; y: number; k: number };
}

interface SkillTreeProps {
    data: SkillData;
    onNodeClick: (skillId: string) => void;
    isDevelopment?: boolean;
    selectedNodeId?: string | null;
    /** Animation-script pulse on one node; a new token restarts the pulse. */
    nodeHighlight?: { nodeId: string; color: string; token: number } | null;
    onViewportChange?: (vp: { x: number; y: number; k: number }) => void;
    onDrag?: (nodeId: string, x: number, y: number) => void;
    onCreateLink?: (sourceId: string, targetId: string) => void;
    ref?: React.Ref<SkillTreeHandle>;
}

const nodeTypes = { skill: SkillNodeView };
const edgeTypes = { skill: SkillEdgeView };

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

/**
 * Returns the cached node when the freshly built one is equivalent, preserving object
 * identity so React Flow skips re-rendering and re-measuring nodes that didn't change
 * (e.g. only the selection moved — a rebuild of every node drops frames from a
 * concurrently running pan animation).
 */
export function reuseSkillFlowNode(
    cached: SkillFlowNode | undefined,
    built: SkillFlowNode,
): SkillFlowNode {
    if (!cached) return built;
    const a = cached.data;
    const b = built.data;
    const unchanged =
        cached.position.x === built.position.x &&
        cached.position.y === built.position.y &&
        cached.width === built.width &&
        cached.height === built.height &&
        a.name === b.name &&
        a.hasLinkedGuide === b.hasLinkedGuide &&
        a.isSelected === b.isSelected &&
        a.isDevelopment === b.isDevelopment &&
        a.highlight?.color === b.highlight?.color &&
        a.highlight?.token === b.highlight?.token;
    return unchanged ? cached : built;
}

const SkillTreeFlow: React.FC<SkillTreeProps> = ({ data, onNodeClick, isDevelopment = false, selectedNodeId = null, nodeHighlight = null, onViewportChange, onDrag, onCreateLink, ref }) => {
    const { setViewport, getViewport } = useReactFlow();
    // Tracks the last applied/reported viewport so renders don't re-apply a stale one
    // and programmatic setViewport calls don't echo back through onViewportChange.
    const prevViewportRef = useRef<{ x: number; y: number; k: number } | null>(
        data.viewport ? { ...data.viewport } : null,
    );

    // Settler for the in-flight animateTo promise. d3 never settles the promise of an
    // interrupted transition, so user gestures and superseding animations resolve it
    // false through here instead.
    const settleAnimationRef = useRef<((completed: boolean) => void) | null>(null);

    // Deliberately leaves prevViewportRef alone: the animation's end fires onMoveEnd,
    // which syncs the final viewport back to app state exactly like a manual drag.
    useImperativeHandle(
        ref,
        () => ({
            animateTo: (vp, duration) => {
                settleAnimationRef.current?.(false);
                return new Promise<boolean>(resolve => {
                    const settle = (completed: boolean) => {
                        if (settleAnimationRef.current === settle) settleAnimationRef.current = null;
                        resolve(completed);
                    };
                    settleAnimationRef.current = settle;
                    void setViewport({ x: vp.x, y: vp.y, zoom: vp.k }, { duration, ease: easeOutCubic })
                        .then(() => settle(true));
                });
            },
            getViewport: () => {
                const vp = getViewport();
                return { x: vp.x, y: vp.y, k: vp.zoom };
            },
        }),
        [setViewport, getViewport],
    );

    // Cache of the previously built flow nodes, keyed by id, for identity reuse.
    const flowNodeCacheRef = useRef<Map<string, SkillFlowNode>>(new Map());

    const flowNodes: SkillFlowNode[] = useMemo(
        () => {
            const cache = flowNodeCacheRef.current;
            const nextCache = new Map<string, SkillFlowNode>();
            const nodes = data.nodes.map(n => {
                const built: SkillFlowNode = {
                    id: n.id,
                    type: 'skill' as const,
                    // fx/fy are node centers; nodeOrigin [0.5, 0.5] keeps that interpretation
                    position: { x: n.fx ?? 0, y: n.fy ?? 0 },
                    width: NODE_DIMENSIONS.width,
                    height: NODE_DIMENSIONS.height,
                    data: {
                        name: n.name,
                        hasLinkedGuide: SkillPanelInteractor.leafHasLinkedGuide(data, n.id),
                        isSelected: n.id === selectedNodeId,
                        isDevelopment,
                        highlight:
                            nodeHighlight && nodeHighlight.nodeId === n.id
                                ? { color: nodeHighlight.color, token: nodeHighlight.token }
                                : undefined,
                    },
                };
                const node = reuseSkillFlowNode(cache.get(n.id), built);
                nextCache.set(n.id, node);
                return node;
            });
            flowNodeCacheRef.current = nextCache;
            return nodes;
        },
        // Depend on the data slices that affect nodes, not `data` itself: every interactor
        // patch (viewport sync, selection, etc.) produces a new top-level `data` object, and
        // rebuilding nodes unnecessarily makes React Flow re-measure them (edges vanish until
        // handle bounds return). reuseSkillFlowNode narrows that further: within a rebuild,
        // only nodes whose content actually changed get new identities.
        [data.nodes, data.documents, selectedNodeId, isDevelopment, nodeHighlight],
    );

    const flowEdges: Edge[] = useMemo(
        () =>
            data.links.map(l => ({
                id: l.id ?? `${l.source}->${l.target}`,
                source: l.source,
                target: l.target,
                type: 'skill' as const,
            })),
        [data.links],
    );

    // React Flow owns node positions during drags; sync them back from app state otherwise.
    const [nodes, setNodes, onNodesChange] = useNodesState(flowNodes);
    useEffect(() => {
        setNodes(flowNodes);
    }, [flowNodes, setNodes]);

    // Apply externally-driven viewport changes from in-memory data (only if different).
    // Centering animations go through the animateTo handle instead and never touch this.
    useLayoutEffect(() => {
        const vp = data.viewport;
        if (!vp) return;
        const prev = prevViewportRef.current;
        if (prev && prev.x === vp.x && prev.y === vp.y && prev.k === vp.k) return;
        prevViewportRef.current = { ...vp };
        setViewport({ x: vp.x, y: vp.y, zoom: vp.k });
    }, [data.viewport, setViewport]);

    const handleMoveStart = useCallback((event: MouseEvent | TouchEvent | null) => {
        // A source event means a user gesture (programmatic transitions pass null);
        // it interrupts any in-flight animateTo transition, which d3 abandons without
        // ever settling its promise.
        if (event) settleAnimationRef.current?.(false);
    }, []);

    const handleMoveEnd = useCallback(
        (_event: unknown, vp: FlowViewport) => {
            const prev = prevViewportRef.current;
            if (prev && prev.x === vp.x && prev.y === vp.y && prev.k === vp.zoom) return;
            prevViewportRef.current = { x: vp.x, y: vp.y, k: vp.zoom };
            onViewportChange?.({ x: vp.x, y: vp.y, k: vp.zoom });
        },
        [onViewportChange],
    );

    const handleNodeClick = useCallback(
        (_event: React.MouseEvent, node: SkillFlowNode) => onNodeClick(node.id),
        [onNodeClick],
    );

    const handleNodeDragStop = useCallback(
        (_event: MouseEvent | TouchEvent, node: SkillFlowNode) => {
            onDrag?.(node.id, node.position.x, node.position.y);
        },
        [onDrag],
    );

    const handleConnect = useCallback(
        (connection: Connection) => {
            if (connection.source && connection.target && connection.source !== connection.target) {
                onCreateLink?.(connection.source, connection.target);
            }
        },
        [onCreateLink],
    );

    return (
        <ReactFlow
            nodes={nodes}
            edges={flowEdges}
            onNodesChange={onNodesChange}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            nodeOrigin={[0.5, 0.5]}
            defaultViewport={data.viewport ? { x: data.viewport.x, y: data.viewport.y, zoom: data.viewport.k } : undefined}
            minZoom={VIEWPORT_ZOOM_LIMITS.min}
            maxZoom={VIEWPORT_ZOOM_LIMITS.max}
            onMoveStart={handleMoveStart}
            onMoveEnd={handleMoveEnd}
            onNodeClick={handleNodeClick}
            onNodeDragStop={handleNodeDragStop}
            onConnect={handleConnect}
            connectionLineComponent={SkillConnectionLine}
            connectionRadius={NODE_DIMENSIONS.hitTestRadius}
            nodesDraggable={isDevelopment}
            nodesConnectable={isDevelopment && !!onCreateLink}
            nodeDragThreshold={5}
            elementsSelectable={false}
            nodesFocusable={false}
            edgesFocusable={false}
            deleteKeyCode={null}
            style={{
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
            }}
        >
            <ArrowMarkerDefs />
            {/* Initial viewport indicator in dev mode */}
            {isDevelopment && data.initialViewport && (
                <ViewportPortal>
                    <div
                        style={{
                            position: 'absolute',
                            left: data.initialViewport.x - 300, // 300 pixels to the left
                            top: data.initialViewport.y - 240, // 240 pixels up
                            width: 600,
                            height: 480,
                            background: 'rgba(128, 128, 128, 0.3)',
                            border: '2px dashed rgba(128, 128, 128, 0.6)',
                            pointerEvents: 'none',
                            zIndex: -1, // Behind nodes and links
                        }}
                    />
                </ViewportPortal>
            )}
        </ReactFlow>
    );
};

export const SkillTree: React.FC<SkillTreeProps> = (props) => (
    <div className="skill-tree-host">
        <ReactFlowProvider>
            <SkillTreeFlow {...props} />
        </ReactFlowProvider>
    </div>
);
